/**
 * Leaguemate Scouting — S2 affinity fingerprint: BELIEVERS ONLY (pure, no I/O).
 *
 * "Players this manager always rosters." Built from roster data alone (S1's
 * sampled franchises) — the "don't-value / trade-away" half needs the
 * transaction pull and is S3 (v1 spec §4 slice-split). Any consumer/UI must
 * frame this as believers only.
 *
 * Method (v1 spec §4):
 *   1. Lineage collapse — group a manager's sampled leagues into FRANCHISES by
 *      walking previous_league_id, deduping season-incarnations (e.g. 2025-CSW
 *      and 2026-CSW are one franchise, counted once).
 *   2. weightedRosterRate per player = Σ(franchiseSim · present) / Σ(franchiseSim),
 *      denominator = franchises (NOT incarnations). Presence = the latest
 *      season's roster per franchise.
 *   3. tenure (seasons held within a franchise) is a strength annotation, not
 *      extra denominator weight.
 *   4. Resolve player_ids via the Sleeper players map; keep QB/RB/WR/TE only.
 *   5. Tag with FantasyCalc value/position; expose leverage = rate × value-pct.
 *   6. Honesty: sample size per believer; flag "limited data" under a franchise
 *      floor.
 */

import {
  SKILL_POSITIONS,
  AFFINITY_MIN_FRANCHISES,
  AFFINITY_HIGH_CONFIDENCE_FRANCHISES,
} from "./scoutConfig.js";

const SKILL = new Set(SKILL_POSITIONS);

/**
 * Group sampled-league roster entries into franchises by previous_league_id.
 * Each entry: { league_id, previous_league_id, league_name, season, similarity,
 *               players[], found }.
 * Only links that stay WITHIN the sampled set merge a franchise (a chain to a
 * league we didn't sample is just this franchise's tail).
 *
 * Returns franchises: Array<{
 *   leagueIds: string[], incarnations: Array<{season, similarity, players:Set, league_id}>,
 *   usable: boolean, similarity: number, latestSeason: string|null,
 *   latestPlayers: Set<string>, tenureByPlayer: Map<string, number>
 * }>
 */
export function collapseLineages(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const idSet = new Set(list.map((e) => String(e.league_id)));
  const parent = new Map();
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };
  const union = (a, b) => { parent.set(find(a), find(b)); };

  for (const e of list) parent.set(String(e.league_id), String(e.league_id));
  for (const e of list) {
    const prev = e.previous_league_id != null ? String(e.previous_league_id) : null;
    if (prev && idSet.has(prev)) union(String(e.league_id), prev);
  }

  const groups = new Map(); // root -> entries[]
  for (const e of list) {
    const root = find(String(e.league_id));
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(e);
  }

  const franchises = [];
  for (const grp of groups.values()) {
    const incarnations = grp
      .map((e) => ({
        league_id: String(e.league_id),
        season: e.season != null ? String(e.season) : "",
        similarity: e.similarity ?? 0,
        players: new Set((e.players || []).map(String)),
      }))
      .sort((a, b) => b.season.localeCompare(a.season)); // latest season first

    const withData = incarnations.filter((i) => i.players.size > 0);
    const usable = withData.length > 0;
    const presence = withData[0] || null; // latest incarnation that has roster data

    // tenure: # of data-bearing incarnations in this franchise holding the player
    const tenureByPlayer = new Map();
    for (const inc of withData) {
      for (const pid of inc.players) tenureByPlayer.set(pid, (tenureByPlayer.get(pid) || 0) + 1);
    }

    franchises.push({
      leagueIds: grp.map((e) => String(e.league_id)),
      incarnations,
      usable,
      similarity: presence ? presence.similarity : (incarnations[0]?.similarity ?? 0),
      latestSeason: presence ? presence.season : (incarnations[0]?.season ?? null),
      latestPlayers: presence ? presence.players : new Set(),
      tenureByPlayer,
    });
  }
  return franchises;
}

const playerName = (meta, fallback) => {
  if (!meta) return fallback || null;
  const n = `${meta.first_name || ""} ${meta.last_name || ""}`.trim();
  return n || meta.full_name || fallback || null;
};

/**
 * Compute the believer list from collapsed franchises.
 *
 * @param franchises   from collapseLineages
 * @param playersMap   Sleeper players map (id → {first_name,last_name,position,team,age})
 * @param valueMap     Map<sleeperId, {value, position, overallRank, positionRank, percentile}>
 * @returns { believers, franchiseCount, usableFranchiseCount, limitedData, confidence }
 */
export function computeBelievers(franchises, playersMap = {}, valueMap = new Map()) {
  const usable = (franchises || []).filter((f) => f.usable);
  const denom = usable.reduce((s, f) => s + (f.similarity || 0), 0);

  // Aggregate presence across franchises (presence = latest roster).
  const agg = new Map(); // pid -> { numer, presentIn, tenure }
  for (const f of usable) {
    for (const pid of f.latestPlayers) {
      const a = agg.get(pid) || { numer: 0, presentIn: 0, tenure: 0 };
      a.numer += f.similarity || 0;
      a.presentIn += 1;
      a.tenure = Math.max(a.tenure, f.tenureByPlayer.get(pid) || 1);
      agg.set(pid, a);
    }
  }

  const believers = [];
  for (const [pid, a] of agg) {
    const meta = playersMap[pid];
    const pos = meta?.position || valueMap.get(pid)?.position || null;
    // Skill assets only — drops DEF (team-code ids), K, IDP, and ids we can't
    // classify (no players-map entry and no value-map position).
    if (!pos || !SKILL.has(pos)) continue;

    const v = valueMap.get(pid) || null;
    const rate = denom > 0 ? a.numer / denom : 0;
    const percentile = v?.percentile ?? 0;
    believers.push({
      player_id: pid,
      name: playerName(meta, v?.name),
      position: pos,
      team: meta?.team || null,
      age: meta?.age ?? null,
      value: v?.value ?? null,
      overallRank: v?.overallRank ?? null,
      positionRank: v?.positionRank ?? null,
      rate: Math.round(rate * 1000) / 1000,
      presentIn: a.presentIn,
      totalFranchises: usable.length,
      tenure: a.tenure,
      leverageScore: Math.round(rate * percentile * 1000) / 1000,
    });
  }

  // Primary rank: weightedRosterRate desc, then value, then better overallRank.
  believers.sort(
    (x, y) =>
      y.rate - x.rate ||
      (y.value ?? -1) - (x.value ?? -1) ||
      (x.overallRank ?? 1e9) - (y.overallRank ?? 1e9) ||
      String(x.player_id).localeCompare(String(y.player_id))
  );

  const usableCount = usable.length;
  const limitedData = usableCount < AFFINITY_MIN_FRANCHISES;
  const confidence = limitedData
    ? "limited"
    : usableCount >= AFFINITY_HIGH_CONFIDENCE_FRANCHISES
    ? "high"
    : "medium";

  return {
    believers,
    franchiseCount: (franchises || []).length,
    usableFranchiseCount: usableCount,
    limitedData,
    confidence,
  };
}

/**
 * End-to-end per-manager believers: collapse lineages → compute believers.
 * `affinityEntries` is S1's per-manager affinity roster list (now carrying
 * previous_league_id).
 */
export function buildManagerBelievers(affinityEntries, playersMap, valueMap) {
  const franchises = collapseLineages(affinityEntries);
  return computeBelievers(franchises, playersMap, valueMap);
}

/** Alternate ordering: "valuable believers" = rate × value-percentile desc. */
export function sortByLeverage(believers) {
  return [...(believers || [])].sort(
    (x, y) => y.leverageScore - x.leverageScore || y.rate - x.rate
  );
}
